import { useCallback } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import {
    useInputValues,
    useInputSetters
} from "@/components/usage-based-pricing-calculator/src/hooks";
import { wacToken } from "@/libraries/design/foundations";

import { useGrowthSliderTracking } from "../hooks/tracking";

import { messages } from "./growth-slider.messages";

const sectionStyles = css({
    paddingBlock: token("space.400"),
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "26px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const sliderContainerStyles = css({
    paddingBlockStart: "44px",
    position: "relative",
    marginBlockEnd: token("space.100")
});

const tooltipStyles = css({
    position: "absolute",
    insetBlockStart: "4px", // change tooltip positioning here
    transform: "translateX(-50%)",
    backgroundColor: wacToken("color.primary.black"),
    color: wacToken("color.neutral.light0"),
    padding: "4px 8px",
    borderRadius: "4px",
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    pointerEvents: "none"
});

const sliderBaseStyles = css({
    "width": "100%",
    "height": "6px",
    "borderRadius": "3px",
    "appearance": "none",
    "WebkitAppearance": "none",
    "outline": "none",

    "&::-webkit-slider-thumb": {
        appearance: "none",
        WebkitAppearance: "none",
        width: "24px",
        height: "24px",
        borderRadius: "50%",
        cursor: "pointer",
        border: "none",
        boxShadow: token("elevation.shadow.raised")
    }
});

const sliderGreyThumbStyles = css({
    "&::-webkit-slider-thumb": {
        backgroundColor: wacToken("color.neutral.light400")
    }
});

const sliderBlueThumbStyles = css({
    "&::-webkit-slider-thumb": {
        backgroundColor: wacToken("color.primary.blue")
    }
});

const labelsStyles = css({
    display: "flex",
    justifyContent: "space-between",
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    color: wacToken("color.neutral.light700"),
    marginBlockEnd: token("space.300")
});

export const GrowthSlider = () => {
    const intl = useIntl();
    const { orgChangePercent, orgUserCount } = useInputValues();
    const { setOrgChangePercent } = useInputSetters();
    const { fireSliderClickedEvent } = useGrowthSliderTracking();

    const handleSliderChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const percent = parseInt(e.target.value, 10);
            // Convert percent back to multiplier: multiplier = 1 + percent/100
            setOrgChangePercent(1 + percent / 100);
        },
        [setOrgChangePercent]
    );

    const handleSliderClick = useCallback(() => {
        fireSliderClickedEvent(orgChangePercent);
    }, [fireSliderClickedEvent, orgChangePercent]);

    // orgChangePercent is stored as a multiplier (0..3), where 1.0 = 0% change
    // Slider range: -100 to 100 (percentage), 0 = no change (center)
    const sliderPercent = Math.round((orgChangePercent - 1) * 100);
    const displayPercent = sliderPercent;
    // Thumb position: 0 at left (-100%), 50% at center (0%), 100% at right (+100%)
    const thumbPosition = (sliderPercent + 100) / 2;

    return (
        <div css={sectionStyles}>
            <Heading size="medium" as="h2">
                <span css={titleStyles}>
                    <FormattedMessage {...messages.growthSliderTitle} />
                </span>
            </Heading>
            <div css={sliderContainerStyles}>
                <div css={tooltipStyles} style={{ left: `${thumbPosition}%` }}>
                    {displayPercent > 0 ? `+${displayPercent}` : displayPercent}
                    %
                </div>
                <input
                    css={[
                        sliderBaseStyles,
                        orgUserCount > 0
                            ? sliderBlueThumbStyles
                            : sliderGreyThumbStyles
                    ]}
                    type="range"
                    min="-100"
                    max="100"
                    step="1"
                    value={sliderPercent}
                    style={{
                        background:
                            orgUserCount > 0
                                ? `linear-gradient(to right, ${wacToken(
                                      "color.primary.blue"
                                  )} 0%, ${wacToken(
                                      "color.primary.blue"
                                  )} ${thumbPosition}%, ${wacToken(
                                      "color.neutral.light300"
                                  )} ${thumbPosition}%, ${wacToken(
                                      "color.neutral.light300"
                                  )} 100%)`
                                : `linear-gradient(to right, ${wacToken(
                                      "color.neutral.light400"
                                  )} 0%, ${wacToken(
                                      "color.neutral.light400"
                                  )} ${thumbPosition}%, ${wacToken(
                                      "color.neutral.light300"
                                  )} ${thumbPosition}%, ${wacToken(
                                      "color.neutral.light300"
                                  )} 100%)`
                    }}
                    onChange={handleSliderChange}
                    onClick={handleSliderClick}
                    aria-label={intl.formatMessage(
                        messages.growthSliderAriaLabel
                    )}
                />
            </div>

            <div css={labelsStyles}>
                <span>
                    <FormattedMessage {...messages.sliderLabelShrinks} />
                </span>
                <span>
                    <FormattedMessage {...messages.sliderLabelNoChange} />
                </span>
                <span>
                    <FormattedMessage {...messages.sliderLabelGrows} />
                </span>
            </div>
        </div>
    );
};
